import React from 'react'
import { Form, Row, Col } from 'react-bootstrap'
import { snakeCase } from 'lodash'
import { isAreaArticleType, isDisabledArticleType } from '../ArticleParse'
import { ArticleType } from '../../common/types'
import type { ArticleTypeValue, PriorityValue, DividerLineModeValue } from '../../common/types'

type Values = {
  articleType: ArticleTypeValue
  priority: PriorityValue
  columnSpaceExistence: boolean
  dividerLineMode: DividerLineModeValue
  article?: { filename?: string } | null
}

type Errors = {
  articleType?: string
  priority?: string
  columnSpaceExistence?: string
  dividerLineMode?: string
}

type Touched = {
  articleType?: boolean
  priority?: boolean
  columnSpaceExistence?: boolean
  dividerLineMode?: boolean
}

type EnumValues = {
  articleTypes: Record<string, string>
  priorities: Record<string, string>
  dividerLineModes: Record<string, string>
}

type Props = {
  values: Values
  errors: Errors
  touched: Touched
  handleChange: React.ChangeEventHandler<HTMLElement>
  handleBlur: React.FocusEventHandler<HTMLElement>
  handleChangeArticleType: React.ChangeEventHandler<HTMLElement>
  handleChangePriority: React.ChangeEventHandler<HTMLElement>
  enumValues: EnumValues
  usedPriorities: Set<string>
  showExperimentalFeatures: boolean
}

export const ArticleTypeAndPriorityFields = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  handleChangeArticleType,
  handleChangePriority,
  enumValues,
  usedPriorities,
  showExperimentalFeatures
}: Props) => {
  return (
    <Row className='mb-2'>
      <Form.Group as={Col}>
        <Form.Label column>記事種別</Form.Label>
        <Col>
          <Form.Select
            name='articleType'
            size='sm'
            value={values.articleType}
            onChange={handleChangeArticleType}
            onBlur={handleBlur}
            disabled={values.articleType === ArticleType.Template && !!values?.article?.filename}
            isInvalid={!!(touched.articleType && errors.articleType)}
          >
            {Object.keys(enumValues.articleTypes).map((articleType) => (
              <option
                key={articleType}
                value={articleType}
                disabled={isDisabledArticleType(values.priority, articleType as ArticleTypeValue)}
              >
                {enumValues.articleTypes[articleType]}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type='invalid'>{errors.articleType}</Form.Control.Feedback>
        </Col>
      </Form.Group>
      <Form.Group as={Col}>
        <Form.Label column>記事優先度</Form.Label>
        <Col>
          <Form.Select
            name='priority'
            size='sm'
            value={values.priority}
            onChange={handleChangePriority}
            disabled={values.articleType === ArticleType.Template}
            onBlur={handleBlur}
            isInvalid={!!(touched.priority && errors.priority)}
          >
            {Object.keys(enumValues.priorities).map((k) => (
              <option key={k} value={snakeCase(k)} disabled={usedPriorities.has(k)}>
                {enumValues.priorities[k]}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type='invalid'>{errors.priority}</Form.Control.Feedback>
        </Col>
      </Form.Group>
      {!isAreaArticleType(values.articleType) && (
        <Form.Group as={Col}>
          <Form.Label column>中段線有無</Form.Label>
          <Col>
            <Form.Check
              type='switch'
              id='columnSpaceExistenceSwitch'
              label='中段線あり'
              name='columnSpaceExistence'
              checked={values.columnSpaceExistence}
              onChange={(e) => {
                console.log('columnSpaceExistence', e.target.checked)
                handleChange(e)
              }}
              onBlur={handleBlur}
              isInvalid={!!(touched.columnSpaceExistence && errors.columnSpaceExistence)}
            />
          </Col>
        </Form.Group>
      )}
      {isAreaArticleType(values.articleType) && showExperimentalFeatures && (
        <Form.Group as={Col}>
          <Form.Label column>仕切り罫</Form.Label>
          <Col>
            <Form.Select
              name='dividerLineMode'
              size='sm'
              value={values.dividerLineMode}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={!!(touched.dividerLineMode && errors.dividerLineMode)}
            >
              {Object.keys(enumValues.dividerLineModes).map((k) => (
                <option key={k} value={snakeCase(k)}>
                  {enumValues.dividerLineModes[k]}
                </option>
              ))}
            </Form.Select>
          </Col>
        </Form.Group>
      )}
    </Row>
  )
}
